import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/context/AuthContext';
import { useToast } from '@/components/organisms/feedback/use-toast';
import { useConsultantStatementInterestsHolds } from '@/services/admin-services/query/useConsultantStatementInterestsHolds';
import { useAddStatementInterestsHold } from '@/services/admin-services/mutations/useAddStatementInterestsHold';
import { useUpdateStatementInterestsHold } from '@/services/admin-services/mutations/useUpdateStatementInterestsHold';
import { useDeleteStatementInterestsHold } from '@/services/admin-services/mutations/useDeleteStatementInterestsHold';
import {
    findInterestsHoldForDate,
    getActiveInterestsHold,
    getNextUpcomingInterestsHold,
    getInterestsHoldsForMonth,
    sortInterestsHolds,
    toIsoDay,
} from '@/utils/interestsHolds';

const EMPTY_HOLD_FORM = { startDate: '', endDate: '', comment: '' };

// Les gels modifient le calcul des intérêts : on rafraîchit aussi les vues relevé/débordement actives.
const HOLD_REFETCH_QUERIES = [
    'ConsultantStatementInterestsHolds',
    'StatementOperationsAndInterests',
    'GetConsultantStatement',
];

export const useStatementInterestsHolds = ({ consultantUuid, isAdmin = true }) => {
    const { hasPrivileges } = useAuthContext();
    const { toast } = useToast();
    const [t] = useTranslation('global');
    const canManageHolds = !!(isAdmin && hasPrivileges?.('ADMIN_STATEMENT'));

    const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
    const [editingHold, setEditingHold] = useState(null);
    const [holdToDelete, setHoldToDelete] = useState(null);

    const form = useForm({ defaultValues: EMPTY_HOLD_FORM, mode: 'onChange' });

    const { data, loading, error, refetch } = useConsultantStatementInterestsHolds(consultantUuid, !canManageHolds);
    const { addStatementInterestsHold, loading: addLoading } = useAddStatementInterestsHold();
    const { updateStatementInterestsHold, loading: updateLoading } = useUpdateStatementInterestsHold();
    const { deleteStatementInterestsHold, loading: deleteLoading } = useDeleteStatementInterestsHold();

    const holds = useMemo(() => sortInterestsHolds(data), [data]);

    const activeHold = useMemo(() => getActiveInterestsHold(holds), [holds]);
    const upcomingHold = useMemo(() => getNextUpcomingInterestsHold(holds), [holds]);

    const getHoldForDate = (date) => findInterestsHoldForDate(date, holds);

    const getHoldsForMonth = (year, monthIndex) => getInterestsHoldsForMonth(holds, year, monthIndex);

    const openCreateHold = () => {
        if (!canManageHolds) return;
        setEditingHold(null);
        form.reset({ ...EMPTY_HOLD_FORM, startDate: toIsoDay(new Date()) });
        setIsHoldModalOpen(true);
    };

    const openEditHold = (hold) => {
        if (!canManageHolds) return;
        setEditingHold(hold);
        form.reset({
            startDate: toIsoDay(hold?.startDate) || '',
            endDate: toIsoDay(hold?.endDate) || '',
            comment: hold?.comment || '',
        });
        setIsHoldModalOpen(true);
    };

    const closeHoldModal = () => {
        setIsHoldModalOpen(false);
        setEditingHold(null);
        form.reset(EMPTY_HOLD_FORM);
    };

    const submitHold = async (values) => {
        if (!canManageHolds || !consultantUuid) return;
        const request = {
            startDate: values.startDate,
            endDate: values.endDate || null,
            comment: (values.comment || '').trim() || null,
        };
        try {
            if (editingHold) {
                await updateStatementInterestsHold({
                    variables: { uuid: editingHold.uuid, request },
                    refetchQueries: HOLD_REFETCH_QUERIES,
                });
                toast({ variant: 'success', title: 'success.backendOperation.update', duration: 3000 });
            } else {
                await addStatementInterestsHold({
                    variables: { consultantUuid, request },
                    refetchQueries: HOLD_REFETCH_QUERIES,
                });
                toast({ variant: 'success', title: 'success.backendOperation.add', duration: 3000 });
            }
            closeHoldModal();
        } catch (err) {
            toast({ variant: 'error', title: 'error.requestFailed', description: err?.message, duration: 5000 });
        }
    };

    const handleSubmitHold = form.handleSubmit(submitHold);

    const requestDeleteHold = (hold) => {
        if (!canManageHolds) return;
        setIsHoldModalOpen(false);
        setEditingHold(null);
        setHoldToDelete(hold);
    };

    const closeDeleteHoldDialog = () => setHoldToDelete(null);

    const confirmDeleteHold = async () => {
        if (!holdToDelete) return;
        try {
            await deleteStatementInterestsHold({
                variables: { uuid: holdToDelete.uuid },
                refetchQueries: HOLD_REFETCH_QUERIES,
            });
            toast({ variant: 'success', title: 'success.backendOperation.delete', duration: 3000 });
        } catch (err) {
            toast({ variant: 'error', title: 'error.requestFailed', description: err?.message, duration: 5000 });
        } finally {
            setHoldToDelete(null);
        }
    };

    const validateEndDate = (endDate, values) =>
        !endDate || !values.startDate || endDate >= values.startDate
        || t('consultants.statement.interestsHolds.endDateBeforeStartDate');

    return {
        t,
        canManageHolds,
        holds,
        loading,
        error,
        refetch,
        mutationLoading: addLoading || updateLoading || deleteLoading,
        activeHold,
        upcomingHold,
        getHoldForDate,
        getHoldsForMonth,
        form,
        validateEndDate,
        isHoldModalOpen,
        editingHold,
        openCreateHold,
        openEditHold,
        closeHoldModal,
        handleSubmitHold,
        holdToDelete,
        requestDeleteHold,
        closeDeleteHoldDialog,
        confirmDeleteHold,
    };
};

export default useStatementInterestsHolds;
